import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { usePortal } from '../contexts/PortalContext';
const EASE = [0.23, 1, 0.32, 1] as const;
interface ContactPortalProps {
  open: boolean;
  onClose: () => void;
}
export function ContactPortal({
  open,
  onClose
}: ContactPortalProps) {
  const {
    activeProject,
    contactPortal,
    notify
  } = usePortal();
  const [writing, setWriting] = useState(false);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const lead = activeProject.team[0];
  function reset() {
    onClose();
    setWriting(false);
    setMessage('');
    setSent(false);
  }

  /** The X button and backdrop both come through here, so a drafted-but-unsent
   *  message is never lost without the person choosing to lose it. */
  function close() {
    if (writing && message.trim()) {
      const discard = window.confirm('Discard this message? It has not been sent to Portal.');
      if (!discard) return;
    }
    reset();
  }
  function send(event: React.FormEvent) {
    event.preventDefault();
    const body = message.trim();
    if (!body) return;
    contactPortal(body);
    setMessage('');
    setSent(true);
    notify('Message sent to Portal');
  }
  return <AnimatePresence data-id="element-343" data-mp="59ssjx4euwed">
      {open && <motion.div key="contact" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-ink/50 px-6 py-10" role="dialog" aria-modal="true" aria-label="Contact Portal" data-id="element-344" data-mp="5auckr3emt88">
          <motion.div initial={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.25,
        ease: EASE
      }} className="w-full max-w-lg border border-ink bg-paper px-7 py-9 sm:px-10" data-id="element-345" data-mp="o7ltitvh4w94">
            <div className="flex items-start justify-between gap-6" data-id="element-346" data-mp="2hzlx3af1wvxj">
              <p className="label m-0 text-graphite" data-id="element-347" data-mp="2dys9ki5itj56">Contact Portal</p>
              <button type="button" onClick={close} aria-label="Close" className="text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-348" data-mp="2rx8fw7zh17ms">
                <XIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-349" data-mp="30x4xxqrcv88v" />
              </button>
            </div>

            {sent ? <>
                <p className="mt-7 font-display text-3xl leading-none" data-id="element-350" data-mp="2wus33xe5cuqv">Message sent</p>
                <p className="mt-5 max-w-sm font-display text-lg text-charcoal" data-id="element-351" data-mp="3aq3xg3oe4j79">
                  {lead ? `${lead.name} will come back to you shortly.` : 'Portal will reply shortly.'}
                </p>
                <button type="button" onClick={close} className="label mt-9 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-352" data-mp="3kk2yt0gn3yy5">
                  Close
                </button>
              </> : <>
                <p className="mt-7 font-display text-3xl leading-tight" data-id="element-353" data-mp="3pbhuqa4uhwon">
                  Need help with this project?
                </p>

                {lead && <div className="mt-8 border-y border-rule py-6" data-id="element-354" data-mp="3fkwteexsee21">
                    <p className="font-display text-2xl leading-none" data-id="element-355" data-mp="3fjlpmf4cd6ax">{lead.name}</p>
                    <p className="label mt-3 text-mute" data-id="element-356" data-mp="3kd6g7m3luy03">{lead.role}</p>
                  </div>}

                {writing ? <form onSubmit={send} className="mt-8" data-id="element-357" data-mp="3bjbeko1ozpp7">
                    <label className="block" data-id="element-358" data-mp="3bjtjsa6c1e99">
                      <span className="label block text-graphite" data-id="element-359" data-mp="2wmawlc9az93x">How can we help?</span>
                      <textarea value={message} onChange={event => setMessage(event.target.value)} rows={4} autoFocus placeholder="Tell us what you need…" className="mt-4 w-full resize-none border-0 border-b border-rule bg-transparent pb-3 font-display text-xl text-ink placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-360" data-mp="31l46yvtamjzv" />
                    </label>
                    <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-361" data-mp="3l8an3js1uy53">
                      <button type="submit" className="label border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-362" data-mp="3h6p3lfl0fllx">
                        Send to Portal
                      </button>
                      <button type="button" onClick={() => setWriting(false)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-363" data-mp="1afqzqg3vh5d5">
                        Cancel
                      </button>
                    </div>
                  </form> : <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-364" data-mp="1mq8mthkqrbl6">
                    <button type="button" onClick={() => setWriting(true)} className="label border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-365" data-mp="1msbioi8kryxt">
                      Send Message
                    </button>
                    <button type="button" onClick={() => notify('Simulated · would open your email client')} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-366" data-mp="13zhe8i8kvp7u">
                      Email Portal
                    </button>
                  </div>}
              </>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}